import {
  SmartToy,
  Person,
  Webhook,
  AdminPanelSettings,
  LocalHospital,
} from '@mui/icons-material'

import type { ActorType, EventLogLevel } from '../../api'

const shortDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

const fullDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

export const formatEventDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'
    return shortDateFormatter.format(date)
  } catch {
    return 'Invalid date'
  }
}

export const formatFullDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'
    return fullDateFormatter.format(date)
  } catch {
    return 'Invalid date'
  }
}

export const getRelativeTime = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMs < 0) return ''
    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return ''
  } catch {
    return ''
  }
}

export const formatEventType = (eventType: string): string => {
  return eventType
    .replace(/\./g, ' ')
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
}

const MAX_METADATA_STRING_LENGTH = 500

export const getMetadataString = (
  metadata: Record<string, unknown> | null,
  key: string,
): string | undefined => {
  const value = metadata?.[key]
  if (typeof value !== 'string') return undefined
  return value.length > MAX_METADATA_STRING_LENGTH
    ? value.slice(0, MAX_METADATA_STRING_LENGTH)
    : value
}

export const LEVEL_CONFIG: Record<EventLogLevel, { color: string; bg: string; label: string }> = {
  error: { color: '#d32f2f', bg: '#fdeded', label: 'Error' },
  warning: { color: '#ed6c02', bg: '#fff4e5', label: 'Warning' },
  info: { color: '#0288d1', bg: '#e5f6fd', label: 'Info' },
}

export const ACTOR_CONFIG: Record<ActorType, { icon: typeof SmartToy; label: string; color: string }> = {
  system: { icon: SmartToy, label: 'System', color: '#7c4dff' },
  user: { icon: Person, label: 'User', color: '#2196f3' },
  webhook: { icon: Webhook, label: 'Webhook', color: '#00bcd4' },
  admin: { icon: AdminPanelSettings, label: 'Admin', color: '#ff5722' },
  doctor: { icon: LocalHospital, label: 'Doctor', color: '#4caf50' },
}
